// The v12.75 purchase flow, for iOS binaries without PurchasesPlugin. Remove
// with the receipt path once binaries below 12.76.0 are forced to update (#301).
import * as orm from "@client/features/orm";
import * as hardware from "@client/hardware";
import type {AppDispatch} from "@client/store";
import {server} from "@reactivated";

export const isActive = () => !hardware.hasPurchasesPlugin();

const publish = (dispatch: AppDispatch, productIds: string[]) => {
    const products = productIds.flatMap((id): hardware.StoreProduct[] => {
        const product = CdvPurchase.store.get(id);
        if (product == null) {
            return [];
        }
        return [
            {
                id,
                displayName: product.title,
                displayPrice: product.getOffer()?.pricingPhases.at(-1)?.price ?? "",
                isEligibleForIntroOffer: false,
            },
        ];
    });
    dispatch({type: "LOAD_APPLE_SUBSCRIPTIONS", products});
    dispatch({
        type: "SET_APPLE_ENTITLEMENTS",
        ownedProductIds: productIds.filter(
            (id) => CdvPurchase.store.get(id)?.owned === true,
        ),
    });
};

let validating: ReturnType<typeof server.core.payments.validate_iap> | null = null;

export const load =
    (productIds: string[], completePendingRegistration: () => Promise<void>) =>
    async (dispatch: AppDispatch) => {
        const {applicationId} = await hardware.getAppInfo();

        CdvPurchase.store.register(
            productIds.map((id) => ({
                id,
                type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
                platform: CdvPurchase.Platform.APPLE_APPSTORE,
            })),
        );
        void CdvPurchase.store.initialize();
        CdvPurchase.store.ready(() => publish(dispatch, productIds));

        void CdvPurchase.store.when().unverified((res) => {
            if (
                res.payload.code ===
                CdvPurchase.ErrorCode.VALIDATOR_SUBSCRIPTION_EXPIRED
            ) {
                const adapter = CdvPurchase.store.getAdapter(
                    CdvPurchase.Platform.APPLE_APPSTORE,
                ) as CdvPurchase.AppleAppStore.Adapter;
                void res.receipt.finish().then(() => adapter.refreshReceipt());
            }
        });

        CdvPurchase.store
            .when()
            .approved((transaction) => void transaction.verify())
            .verified((receipt) => {
                void dispatch(orm.fetchSubscription());
                void receipt.finish();
                publish(dispatch, productIds);
            });

        // oxlint-disable-next-line typescript/no-misused-promises
        CdvPurchase.store.validator = async (body, callback) => {
            const transaction =
                body.transaction as CdvPurchase.Validator.Request.ApiValidatorBodyTransactionApple;

            if (validating == null) {
                await completePendingRegistration();
                validating = server.core.payments.validate_iap({
                    receipt_data: transaction.appStoreReceipt ?? "NOT POPULATED",
                });
                void validating.finally(() => {
                    validating = null;
                });
            }
            const result = await validating;

            if (result.type != "success") {
                callback({ok: false});
            } else if (result.data != null) {
                callback({ok: false, code: result.data});
            } else {
                callback({
                    ok: true,
                    data: {
                        collection: [{id: body.id!}],
                        id: applicationId,
                        ineligible_for_intro_price: [],
                        latest_receipt: true,
                        transaction:
                            {} as CdvPurchase.Validator.Response.NativeTransaction,
                    },
                });
            }
        };
    };

export const purchase = async (
    productId: string,
): Promise<"PURCHASED" | "CANCELLED" | "PENDING" | "ERROR"> => {
    const error = await CdvPurchase.store.get(productId)?.getOffer()?.order();

    // The order only starts the purchase; the validator completes it.
    if (error == null) {
        return "PENDING";
    }
    return error.code === CdvPurchase.ErrorCode.PAYMENT_CANCELLED
        ? "CANCELLED"
        : "ERROR";
};

export const restore = () => CdvPurchase.store.restorePurchases();

export const manage = () => CdvPurchase.store.manageSubscriptions();
